import {CommonState, ItemListState, RemoteActionState} from "../highOrderReducers/commonReducer";

export enum RequestTypeEnum {
  ONE_WAY = 1,
  ROUND_TRIP = 2,
  MULTI_LEG = 3,
}

export interface IBrokerAircraft {
  planeId: number;
  registrationNumber: string;
  flyCompanyId: number;
  operatorName: string;
  planeTypeId: number;
  planeTypeName: string;
  planeCategoryId: number;
  planeCategoryName: string;
  maxPassengers: number;
  manufactureYear: number;
  renovationYear: number;
  preliminaryPrice: number;
  currencyId: number; // Base currency (where preliminaryPrice is calculated)
  currencyName?: string; // Base currency name
  planeCurrencyId?: number; // Plane's original currency
  planeCurrencyName?: string; // Plane's original currency name
  availableFromDate?: string;
  availableToDate?: string;
  arrivalDate?: string; // Calculated arrival date/time in Y-m-d H:i:s format
  adjustedDepartureDateTime?: string; // Adjusted departure date/time in Y-m-d H:i:s format (if requested time conflicts with event)
  routeErrorMessage?: string;
  positioningDurationMinutes: number;
  positioningDistanceKm: number;
  positioningTechnicalStopCount: number;
  positioningFromAirportId?: number; // Airport where aircraft is located before positioning (at requested time if no adjustments, or at adjusted time if adjustments made)
  positioningDepartureDate?: string; // Exact datetime when positioning flight departs (YYYY-MM-DD HH:MM:SS) - calculated by the calculator considering all gaps and turnarounds
  flightDurationMinutes: number;
  lastLegFlightDurationMinutes?: number; // Duration of the last leg only (for multileg routes)
  flightDistanceKm: number;
  totalDurationMinutes: number;
  technicalStopCount: number;
  refuelingCount: number;
  baseAirportId: number;
  baseAirportIcao: string;
  baseAirportIata: string;
  baseAirportName: string;
  amenities?: {
    airAmbulance: boolean;
    animalsAboard: boolean;
    smokingAboard: boolean;
    airCargoFreight: boolean;
    wifi: boolean;
    ovenAboard: boolean;
  };
}

export interface IOneWayRequestPayload {
  airportFromId: number;
  airportToId: number;
  departureDate: Date;
  pax: number;
  planeCategoryIds: number[];
  companionsAllowed: boolean;
  comment: string;
  companyIds: number[];
}

export interface IRoundTripRequestPayload {
  airportFromId: number;
  airportToId: number;
  departureDateFrom: Date;
  paxFrom: number;
  departureDateTo: Date;
  paxTo: number;
  planeCategoryIds: number[];
  companionsAllowed: boolean;
  comment: string;
  companyIds: number[];
}

type MultiLegRequestItem = {
  airportFromId: number;
  airportToId: number;
  departureDate: Date;
  pax: number;
}

export interface IMultiLegRequestPayload {
  routes: MultiLegRequestItem[];
  planeCategoryIds: number[];
  companionsAllowed: boolean;
  comment: string;
  companyIds: number[];
}

export interface IBrokerAircraftSearchPayload {
  originAirportId: number;
  destinationAirportId: number;
  departureDate?: string; // YYYY-MM-DD
  departureTime?: string; // HH:MM
  pax: number;
  paxTo?: number; // PAX count for return flight (for round trips)
  multilegRoutes?: Array<{
    airportFromId: number;
    airportToId: number;
    departureDate: string; // YYYY-MM-DD
    departureTime?: string; // HH:MM
    pax: number;
  }>; // For multileg flights: all legs with their PAX counts
  planeCategoryIds?: number[];
  planeModel?: string;
  manufactureYearFrom?: number;
  manufactureYearTo?: number;
  renovationYearFrom?: number;
  renovationYearTo?: number;
  positioningDurationFromHours?: number;
  positioningDurationToHours?: number;
  landingCountFrom?: number;
  landingCountTo?: number;
  refuelingCountFrom?: number;
  refuelingCountTo?: number;
  updatedAtFrom?: string;
  amenities?: string[]; // air_ambulance, animals_aboard, smoking_aboard, air_cargo_freight, wifi, oven_aboard
  operatorIds?: number[];
  planeIds?: number[];
  page: number;
  limit?: number;
}

export interface ISetBrokerAircraftSearchResultPayload {
  aircraft: IBrokerAircraft[];
  totalCount: number;
  errorMessage?: string;
  aircraftEntities?: any[]; // BrokerAircraftEntity[] для сохранения полных объектов
  originAirport?: {
    code: string;
    location_and_name: string;
  };
  destinationAirport?: {
    code: string;
    location_and_name: string;
  };
}

export interface ISearchParams {
  airportFromId?: number;
  airportToId?: number;
  departureDate?: Date;
  departureTime?: string;
  pax?: number;
  paxTo?: number; // PAX count for return flight (for round trips)
  multilegRoutes?: Array<{
    airportFromId: number;
    airportToId: number;
    departureDate: string; // YYYY-MM-DD
    departureTime?: string; // HH:MM
    pax: number;
  }>; // For multileg flights: all legs with their PAX counts
  companionsAllowed?: boolean;
  airportFrom?: {
    code: string;
    location_and_name: string;
  };
  airportTo?: {
    code: string;
    location_and_name: string;
  };
  timezoneOffset?: number;
}

export interface INewRequestInitialState extends CommonState<unknown>{
  oneWayRequest: RemoteActionState,
  roundTripRequest: RemoteActionState,
  multiLegRequest: RemoteActionState,
  brokerAircraftSearch: ItemListState<IBrokerAircraft>,
  lastSearchParams?: ISearchParams;
  /** Full payload of the last broker aircraft search (for analytics: filters applied). */
  lastSearchPayload?: IBrokerAircraftSearchPayload;
  brokerAircraftEntities?: any[]; // BrokerAircraftEntity[] для сохранения полных объектов
}